import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimItemLineService } from '../../services';

const toCents = (dollars: number | null | undefined) =>
  dollars === null || dollars === undefined ? null : Math.round(dollars * 100);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-line-adjust-dialog',
  templateUrl: './claim-item-line-adjust.dialog.html',
  standalone: false,
})
export class ClaimItemLineAdjustDialog {
  constructor(
    private formBuilder: FormBuilder,
    private lineSvc: ClaimItemLineService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;
  @Input() item: Claim.ClaimItem.Model | null = null;

  /** The tenant's units, passed in by the charges table. */
  @Input() units: Claim.ClaimItem.Line.Units = { units: [] };

  @Output() saved = new EventEmitter<string>();

  sources = Object.values(Claim.ClaimItem.Line.RecommendationSource);

  line: Claim.ClaimItem.Line.Model | null = null;
  history: Claim.ClaimItem.Line.Adjustment[] = [];
  saving = false;
  /** History only, for staff who can see adjustments but not make them. */
  readOnly = false;

  // Dollars here, cents on save.
  form = this.formBuilder.group({
    allowedQuantity: [null as number | null, Validators.min(0)],
    allowedRate: [null as number | null, Validators.min(0)],
    allowedAmount: [null as number | null, Validators.min(0)],
    recommendationSource: [null as Claim.ClaimItem.Line.RecommendationSource | null, Validators.required],
    sourceVersion: [''],
    reason: ['', [Validators.required, Validators.pattern(/\S/)]],
  });

  async openDialog(line: Claim.ClaimItem.Line.Model, readOnly = false) {
    this.line = line;
    this.readOnly = readOnly;
    this.history = [];

    this.form.reset({
      allowedQuantity: line.allowedQuantity,
      allowedRate: line.allowedRate !== null ? line.allowedRate / 100 : null,
      allowedAmount: line.allowedAmount !== null ? line.allowedAmount / 100 : null,
      recommendationSource: line.recommendationSource,
      sourceVersion: '',
      reason: '',
    });

    this.dialog?.open();

    if (this.claim && this.item) {
      const response = await this.lineSvc.adjustments(this.claim.id, this.item.id, line.id).catch(() => null);
      // Another charge may have been opened while this loaded.
      if (this.line !== line) return;
      this.history = response?.items ?? [];

      // Carry the last edition forward so a small edit doesn't drop it.
      const lastEdition = this.history[0]?.sourceVersion;
      if (lastEdition && this.form.controls.sourceVersion.pristine) {
        this.form.controls.sourceVersion.reset(lastEdition);
      }
    }
  }

  close() {
    this.dialog?.close();
  }

  unitLabel(line: Claim.ClaimItem.Line.Model) {
    return Claim.ClaimItem.Line.unitLabelFor(this.units, line) ?? '';
  }

  formatAmount(amount: number | null | undefined) {
    return amount === null || amount === undefined ? '-' : '$' + (amount / 100).toFixed(2);
  }

  async save() {
    if (!this.claim || !this.item || !this.line || this.readOnly || this.form.invalid || this.saving) return;

    const value = this.form.value;
    const body: Claim.ClaimItem.Line.AdjustClaimItemLineRequest = {
      allowedQuantity: value.allowedQuantity ?? null,
      allowedRate: toCents(value.allowedRate),
      allowedAmount: toCents(value.allowedAmount),
      recommendationSource: value.recommendationSource as Claim.ClaimItem.Line.RecommendationSource,
      sourceVersion: value.sourceVersion?.trim() || null,
      reason: (value.reason ?? '').trim(),
      expectedVersion: this.line.adjustmentVersion,
    };

    this.saving = true;
    try {
      // On failure stay open so the adjuster keeps what they entered.
      const result = await this.lineSvc
        .adjust(this.claim.id, this.item.id, this.line.id, body)
        .catch((response: HttpErrorResponse) => {
          if (response?.status === 409) {
            this.toastSvc.show({ type: 'error', ...this.config.adjust.conflict });
          } else {
            const message = response?.error?.message;
            this.toastSvc.show({
              type: 'error',
              title: this.config.adjust.errorTitle,
              text: Array.isArray(message) ? message.join(' ') : message,
            });
          }
          return null;
        });

      if (!result) return;

      this.toastSvc.show({ type: 'success', ...this.config.adjust.saved });
      this.saved.emit(result.id);
      this.close();
    } finally {
      this.saving = false;
    }
  }
}
